import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Loader2,
  KeyRound,
  ArrowLeft,
  ShieldCheck,
  Mail,
  MessageSquare,
  Chrome,
  Eye,
  EyeOff,
  LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  ELoginProviders,
  LoginResponse,
  UserWithLoginsResponse,
  changeLoginPassword,
  getTenantLogins,
  AccountLoginsApiError,
} from "@/lib/account-logins-service";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type View = "list" | "edit";

interface ProviderStyle {
  icon: LucideIcon;
  className: string;
  label: string;
}

const getProviderStyle = (
  providerId: ELoginProviders,
  fallbackLabel: string,
): ProviderStyle => {
  switch (providerId) {
    case ELoginProviders.Email:
      return {
        icon: Mail,
        className:
          "bg-blue-500/15 text-blue-600 border-blue-500/30 hover:bg-blue-500/15",
        label: "Email",
      };
    case ELoginProviders.Sms:
      return {
        icon: MessageSquare,
        className:
          "bg-green-500/15 text-green-600 border-green-500/30 hover:bg-green-500/15",
        label: "SMS",
      };
    case ELoginProviders.Google:
      return {
        icon: Chrome,
        className:
          "bg-red-500/10 text-red-600 border-red-500/30 hover:bg-red-500/10",
        label: "Google",
      };
    default:
      return {
        icon: KeyRound,
        className:
          "bg-muted text-muted-foreground border-border hover:bg-muted",
        label: fallbackLabel,
      };
  }
};

const ChangePasswordsDialog = ({ open, onOpenChange }: Props) => {
  const [view, setView] = useState<View>("list");
  const [users, setUsers] = useState<UserWithLoginsResponse[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedLogin, setSelectedLogin] = useState<LoginResponse | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserWithLoginsResponse | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const rows = useMemo(
    () =>
      users.flatMap((user) =>
        user.logins.map((login) => ({ user, login })),
      ),
    [users],
  );

  const resetEditFields = () => {
    setNewPassword("");
    setConfirmPassword("");
  };

  useEffect(() => {
    if (!open) {
      setView("list");
      setUsers([]);
      setListError(null);
      setSelectedLogin(null);
      setSelectedUser(null);
      resetEditFields();
      setLoadingList(false);
      setSaving(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingList(true);
      setListError(null);
      try {
        const data = await getTenantLogins();
        if (!cancelled) setUsers(data);
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof AccountLoginsApiError
            ? err.status === 401
              ? "Sesión caducada. Vuelve a iniciar sesión."
              : err.message
            : err instanceof Error
              ? err.message
              : String(err);
        setListError(msg);
        if (err instanceof AccountLoginsApiError && err.status === 401) {
          toast.error(msg);
          onOpenChange(false);
        }
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, onOpenChange]);

  const canChangePassword = (login: LoginResponse) =>
    login.providerId !== ELoginProviders.Google && !login.disabledOn;

  const handleSelect = (login: LoginResponse) => {
    const user = users.find((u) => u.logins.some((l) => l.id === login.id)) || null;
    setSelectedUser(user);
    setSelectedLogin(login);
    resetEditFields();
    setView("edit");
  };

  const handleBack = () => {
    setSelectedLogin(null);
    setSelectedUser(null);
    resetEditFields();
    setView("list");
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLogin) return;
    if (!newPassword.trim()) {
      toast.error("Introduce la nueva contraseña.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }
    setSaving(true);
    try {
      await changeLoginPassword(selectedLogin.id, newPassword);
      toast.success("Contraseña actualizada correctamente.");
      handleBack();
    } catch (err) {
      const msg =
        err instanceof AccountLoginsApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[560px] max-h-[90vh] flex flex-col text-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {view === "edit" ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 p-0 -ml-1"
                  onClick={handleBack}
                  disabled={saving}
                  aria-label="Volver"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <ShieldCheck className="h-5 w-5" />
                Cambiar contraseña
              </>
            ) : (
              <>
                <KeyRound className="h-5 w-5" />
                Gestionar contraseñas
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {view === "edit"
              ? "Define la nueva contraseña del login seleccionado."
              : "Selecciona un usuario para modificar su contraseña"}
          </DialogDescription>
        </DialogHeader>

        {view === "edit" && selectedLogin && (
          <div className="rounded-md border bg-muted/30 p-4 space-y-3 mt-4">
            <div className="flex items-center gap-2 min-w-0">
              {(() => {
                const style = getProviderStyle(
                  selectedLogin.providerId,
                  selectedLogin.providerName,
                );
                const Icon = style.icon;
                return (
                  <Badge
                    variant="outline"
                    className={`gap-1 shrink-0 text-sm ${style.className}`}
                  >
                    <Icon className="h-3 w-3" />
                    {style.label}
                  </Badge>
                );
              })()}
              {selectedUser && (
                <span
                  className="truncate text-base font-medium"
                  title={selectedUser.username}
                >
                  {selectedUser.username}
                </span>
              )}
            </div>
            <div className="space-y-0.5">
              <div className="text-sm text-muted-foreground">
                Clave de inicio de sesión: {selectedLogin.providerKey}
              </div>
            </div>
            {selectedUser?.email && (
              <div
                className="text-sm text-muted-foreground truncate"
                title={selectedUser.email}
              >
                Correo electrónico: {selectedUser.email}
              </div>
            )}
          </div>
        )}

        {view === "list" && (
          <div className="flex flex-col flex-1 gap-4 min-h-0">
            <div className="flex-1 min-h-0 rounded-md border overflow-hidden">
              <div className="h-full overflow-auto">
                <div className="min-h-full flex flex-col">
                  {loadingList && (
                    <div className="flex items-center justify-center py-8 text-muted-foreground">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cargando logins...
                    </div>
                  )}
                  {!loadingList && listError && (
                    <p className="text-sm text-destructive py-4">{listError}</p>
                  )}
                  {!loadingList && !listError && rows.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4">
                      No hay logins en este tenant.
                    </p>
                  )}
                  {!loadingList && !listError && rows.length > 0 && (
                    <div className="w-full overflow-x-auto">
                      <Table>
                        <TableHeader className="sticky top-0 z-10 bg-background">
                          <TableRow>
                            <TableHead>Usuario</TableHead>
                            <TableHead>
                              Provider
                            </TableHead>
                            <TableHead>Identificador</TableHead>
                            <TableHead>
                              Acción
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map(({ user, login }) => {
                            const style = getProviderStyle(
                              login.providerId,
                              login.providerName,
                            );
                            const Icon = style.icon;
                            const disabled = !canChangePassword(login);
                            return (
                              <TableRow key={login.id}>
                                <TableCell>
                                  <div className="font-medium">
                                    {user.username}
                                  </div>
                                  {user.email && (
                                    <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                                      {user.email}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={`gap-1 text-xs ${style.className}`}
                                  >
                                    <Icon className="h-3 w-3" />
                                    {style.label}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span
                                      className="truncate text-sm"
                                      title={login.providerKey}
                                    >
                                      {login.providerKey}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={disabled}
                                    title={
                                      login.providerId === ELoginProviders.Google
                                        ? "Gestionado por Google"
                                        : login.disabledOn
                                          ? "Login deshabilitado"
                                          : undefined
                                    }
                                    onClick={() => handleSelect(login)}
                                  >
                                    {login.disabledOn ? "Inactivo" : "Cambiar"}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </div>
        )}

        {view === "edit" && selectedLogin && (
          <form onSubmit={handleSave} className="flex flex-col flex-1 gap-4 min-h-0">
            <div className="flex-1 flex flex-col justify-center gap-6">
              <div className="space-y-3">
                <Label htmlFor="new-password" className="text-sm">Nueva contraseña del login</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    autoFocus
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={saving}
                    className="h-10 pr-11 text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3.5 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNewPassword((v) => !v)}
                    aria-label={showNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    disabled={saving}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                <Label htmlFor="confirm-password" className="text-sm">Confirmar nueva contraseña</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={saving}
                    className="h-10 pr-11 text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3.5 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    disabled={saving}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={handleBack}
                disabled={saving}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Guardar
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordsDialog;



